import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Target, TrendingUp, CheckCircle, Sparkles } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function CareerChangeCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'AI Coach for Career Change - Navigate Your Career Transition | VZMO';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Considering a career change? Get personalized AI coaching to explore options, plan your transition, and build confidence. Available 24/7. Start free.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/coach/career-change');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/coach/career-change';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('career-change-page');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            AI Coach for Career Change
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Feeling stuck or unfulfilled in your career? Get personalized guidance to explore new paths,
            plan your transition, and take confident action. Available 24/7.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            How VZMO Helps with Career Transitions
          </h2>

          <div className="space-y-6">
            <div className="flex items-start">
              <Compass className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Explore Your Options</h3>
                <p className="text-slate-600">
                  Not sure what's next? Work through structured prompts that help you identify your strengths,
                  values, and interests. Discover careers you haven't considered.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Target className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Create Your Transition Plan</h3>
                <p className="text-slate-600">
                  Break down your career change into actionable steps. From skill development to networking
                  to financial planning, get a clear roadmap customized to your situation.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Build Momentum</h3>
                <p className="text-slate-600">
                  Weekly check-ins keep you accountable. Track your progress, celebrate wins, and adjust
                  your plan as you learn and grow.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Navigate Challenges</h3>
                <p className="text-slate-600">
                  Dealing with fear, doubt, or practical obstacles? Work through concerns in private coaching
                  sessions. Get perspective and strategies to move forward.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Common Career Change Scenarios</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Leaving corporate for entrepreneurship
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Switching from tech to creative fields
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Mid-career pivot to new industry
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Returning to work after a break
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Moving into leadership roles
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Pursuing passion projects full-time
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">What You'll Work Through</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Identifying transferable skills
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Researching target industries
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Building new skills and credentials
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Creating a financial runway
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Networking and building connections
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Managing fear and self-doubt
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <Sparkles className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Start Your Career Change Journey</h2>
          <p className="text-lg mb-6 opacity-90">
            Get clarity on your next move. Try VZMO free for 7 days.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
          <p className="mt-4 text-sm opacity-75">No credit card required</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Common Questions About Career Change Coaching</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                I don't know what I want to do next. Can you help?
              </h3>
              <p className="text-slate-600">
                Absolutely. Many people start with VZMO feeling uncertain. Through structured exploration exercises
                and coaching conversations, you'll gain clarity on what matters to you and what possibilities exist.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                How long does a career change typically take?
              </h3>
              <p className="text-slate-600">
                It varies widely depending on your situation. Some transitions happen in months, others take 1-2 years.
                VZMO helps you create a realistic timeline and break it into manageable phases.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Is AI coaching as good as a human career coach?
              </h3>
              <p className="text-slate-600">
                AI coaching excels at helping you think through options, identify patterns, and stay accountable - all at
                a fraction of the cost. For specialized industry knowledge or personal introductions, human coaches may be better.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What if I'm not sure I'm ready to make a change?
              </h3>
              <p className="text-slate-600">
                That's perfectly normal. VZMO can help you explore whether change is right for you. Sometimes the clarity
                you need is realizing you're in the right place - or that it's time to take the leap.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
